import React, { useEffect, useMemo, useState, useRef, useLayoutEffect } from 'react';
import {
  getGTMClientId,
  AnalyticsPlatformProvider,
  useUser,
  useUserMutation,
  useMetaUserData,
  resolvePersistedAnalyticsAttribution,
} from '@skin-seoul/service';
import { usePersonalizationTracker, BridgeApi } from '@skin-seoul/utils';
import { sendCampaignMetaPageView } from './campaignMetaPageView';
import { useCookieConsentStatus } from './ShortcodeCookieConsentProvider';

interface Props {
  children: React.ReactNode;
}

export function ShortcodeAnalyticsPlatformProvider({ children }: Props) {
  const consent = useCookieConsentStatus();
  const { user, isLoading } = useUser();
  const { useConfirmGuest } = useUserMutation();
  const { mutateAsync: confirmGuest } = useConfirmGuest();
  const guestCheck = useRef<{ eventId: string; promise: ReturnType<typeof confirmGuest> } | undefined>(undefined);
  const tracker = usePersonalizationTracker();
  const [webViewConfig, setWebViewConfig] = useState(() => BridgeApi.getAnalyticsConfig());
  const hasAdvertisingConsent = BridgeApi.isWebview()
    ? webViewConfig?.adStorage === 'granted'
    : consent.hasAdvertisingConsent;
  const getMetaUserData = useMetaUserData(user?.userId, user?.email, hasAdvertisingConsent, isLoading);

  const browserEligible = useRef({ eligible: false });
  useLayoutEffect(() => {
    browserEligible.current = { eligible: !isLoading && hasAdvertisingConsent && !BridgeApi.isWebview() };
    return () => {
      browserEligible.current = { eligible: false };
    };
  }, [isLoading, hasAdvertisingConsent, user?.userId, user?.email, webViewConfig]);

  useEffect(
    () => BridgeApi.subscribeToAnalyticsConfigChange(() => setWebViewConfig(BridgeApi.getAnalyticsConfig())),
    [],
  );

  // WordPress does full page reloads on navigation (no SPA routing), so
  // window.location.* and document.referrer are always fresh on mount.
  const platformContext = useMemo(() => {
    const clientId = webViewConfig ? (tracker.getActorId() ?? '') : getGTMClientId();
    const isBrowser = typeof window !== 'undefined';
    const currentPageUrl = isBrowser ? window.location.href : undefined;
    const attribution = resolvePersistedAnalyticsAttribution(currentPageUrl, { hasAdvertisingConsent });

    return {
      platform: webViewConfig?.platform ?? ('Web' as const),
      clientId,
      userId: user?.userId,
      getMetaUserData,
      sendBrowserMetaPageView: async (eventId: string, isEligible: () => boolean) => {
        const snapshot = browserEligible.current;
        const isCurrent = () => snapshot === browserEligible.current && snapshot.eligible && isEligible();
        if (!isCurrent()) {
          return;
        }
        try {
          // The normal profile query treats failures as null; confirm a real guest before loading the pixel.
          if (guestCheck.current?.eventId !== eventId) {
            guestCheck.current = { eventId, promise: confirmGuest() };
          }
          if ((await guestCheck.current.promise) === null && isCurrent()) {
            await sendCampaignMetaPageView(eventId, isCurrent);
          }
        } catch {
          // An unknown profile remains server-only.
        }
      },
      pagePath: currentPageUrl,
      pageTitle: isBrowser ? document.title : undefined,
      pageReferrer: isBrowser ? document.referrer || undefined : undefined,
      consent: {
        analyticsStorage: consent.hasAnalyticsConsent ? ('granted' as const) : ('denied' as const),
        adStorage: hasAdvertisingConsent ? ('granted' as const) : ('denied' as const),
        adUserData: hasAdvertisingConsent ? ('granted' as const) : ('denied' as const),
        adPersonalization: hasAdvertisingConsent ? ('granted' as const) : ('denied' as const),
      },
      osName: webViewConfig?.osName,
      deviceModel: webViewConfig?.deviceModel,
      appVersion: webViewConfig?.appVersion,
      ...attribution,
      debugMode: process.env.NODE_ENV === 'development',
    };
  }, [
    consent.hasAnalyticsConsent,
    hasAdvertisingConsent,
    user?.userId,
    tracker,
    webViewConfig,
    getMetaUserData,
    confirmGuest,
  ]);

  return <AnalyticsPlatformProvider value={platformContext}>{children}</AnalyticsPlatformProvider>;
}
